import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { fetchBanners, fetchHomeBanners } from './bannerSlice';
import { fetchFeaturedProducts, fetchBestSellingProducts, fetchNewArrivals } from './productSlice';
import { fetchHomeTestimonials } from './testimonialSlice';
import { fetchBrands } from './brandSlice';
import { fetchWishlist } from './wishlistSlice';
import { fetchCategories } from './categorySlice';

export const fetchHomePageData = createAsyncThunk(
    'home/fetchHomePageData',
    async (_, { dispatch, rejectWithValue }) => {
        try {
            const results = await Promise.allSettled([
                dispatch(fetchFeaturedProducts(5)),
            ]);

            const errors = results
                .filter(result => result.status === 'rejected')
                .map(result => result.reason);

            if (errors.length > 0) {
                return rejectWithValue({ errors, partial: true });
            }

            return { success: true };
        } catch (error) {
            return rejectWithValue(error.message || 'Failed to fetch home page data');
        }
    }
);

export const fetchLimitedHomeData = createAsyncThunk(
    'home/fetchLimitedHomeData',
    async (_, { dispatch, rejectWithValue }) => {
        try {
            await Promise.allSettled([
                dispatch(fetchHomeBanners(3)),
                dispatch(fetchCategories(3)),
                dispatch(fetchFeaturedProducts(5)),
                dispatch(fetchBrands(8))
            ]);

            return { success: true };
        } catch (error) {
            return rejectWithValue(error.message || 'Failed to fetch limited home data');
        }
    }
);

const homeSlice = createSlice({
    name: 'home',
    initialState: {
        loading: false,
        limitedLoading: false,
        error: null,
        partialErrors: [],
        isPartialData: false,
        lastFetched: null
    },
    reducers: {
        clearHomeError: (state) => {
            state.error = null;
            state.partialErrors = [];
            state.isPartialData = false;
        },
        resetHomeState: (state) => {
            state.loading = false;
            state.limitedLoading = false;
            state.error = null;
            state.partialErrors = [];
            state.isPartialData = false;
            state.lastFetched = null;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchHomePageData.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.partialErrors = [];
                state.isPartialData = false;
            })
            .addCase(fetchHomePageData.fulfilled, (state) => {
                state.loading = false;
                state.lastFetched = new Date().toISOString();
                state.isPartialData = false;
            })
            .addCase(fetchHomePageData.rejected, (state, action) => {
                state.loading = false;
                if (action.payload?.partial) {
                    state.isPartialData = true;
                    state.partialErrors = action.payload.errors || [];
                } else {
                    state.error = action.payload || 'Failed to fetch home data';
                }
            })
            
            .addCase(fetchLimitedHomeData.pending, (state) => {
                state.limitedLoading = true;
                state.error = null;
            })
            .addCase(fetchLimitedHomeData.fulfilled, (state) => {
                state.limitedLoading = false;
                state.lastFetched = new Date().toISOString();
            })
            .addCase(fetchLimitedHomeData.rejected, (state, action) => {
                state.limitedLoading = false;
                state.error = action.payload;
            });
    }
});

export const { clearHomeError, resetHomeState } = homeSlice.actions;
export default homeSlice.reducer;